// WebAuthn helpers for Face ID / Touch ID (passkey) login
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BIOMETRIC_EMAIL_KEY = 'livon_biometric_email';

export const isWebAuthnSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential;

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const base64urlToBuffer = (base64url) => {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const throwApiError = async (res, fallback) => {
  let detail = fallback;
  try {
    const data = await res.json();
    if (data.detail) detail = data.detail;
  } catch (e) { /* ignore */ }
  throw new Error(detail);
};

// Register this device's biometrics (requires logged-in user's JWT token)
export async function registerBiometrics(token) {
  const optRes = await fetch(`${API}/webauthn/register/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ origin: window.location.origin }),
  });
  if (!optRes.ok) await throwApiError(optRes, 'Kunde inte starta registreringen');
  const options = await optRes.json();

  options.challenge = base64urlToBuffer(options.challenge);
  options.user.id = base64urlToBuffer(options.user.id);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }));
  }

  const credential = await navigator.credentials.create({ publicKey: options });

  const payload = {
    origin: window.location.origin,
    credential: {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
        transports: credential.response.getTransports ? credential.response.getTransports() : [],
      },
      clientExtensionResults: credential.getClientExtensionResults
        ? credential.getClientExtensionResults()
        : {},
    },
  };

  const verifyRes = await fetch(`${API}/webauthn/register/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!verifyRes.ok) await throwApiError(verifyRes, 'Verifieringen misslyckades');
  return verifyRes.json();
}

// Log in with biometrics; returns { access_token, refresh_token, user }
export async function authenticateWithBiometrics(email) {
  const optRes = await fetch(`${API}/webauthn/authenticate/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, origin: window.location.origin }),
  });
  if (!optRes.ok) await throwApiError(optRes, 'Kunde inte starta inloggningen');
  const options = await optRes.json();

  options.challenge = base64urlToBuffer(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }));
  }

  const assertion = await navigator.credentials.get({ publicKey: options });

  const payload = {
    email,
    origin: window.location.origin,
    credential: {
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
        signature: bufferToBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle
          ? bufferToBase64url(assertion.response.userHandle)
          : null,
      },
      clientExtensionResults: assertion.getClientExtensionResults
        ? assertion.getClientExtensionResults()
        : {},
    },
  };

  const verifyRes = await fetch(`${API}/webauthn/authenticate/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!verifyRes.ok) await throwApiError(verifyRes, 'Inloggningen misslyckades');
  return verifyRes.json();
}
